/**
 * Affiliate / Creator Program — web admin API client.
 *
 * Talks to the MOBILE backend (`/api/v1/affiliate`), which is where the program
 * lives (the commission hooks are in the mobile user/gift routes). apiClient
 * auto-attaches the logged-in team member's Firebase Bearer token; that email
 * must be in the affiliate admin allowlist (routes/affiliate.py) or admin
 * actions return 403.
 */
import apiClient from "../services/apiClient";
import { mobileAppUri } from "../API/API";

const BASE = `${mobileAppUri}/api/v1/affiliate`;

export interface CommissionTerms {
  signupBountyCents: number;
  firstPaymentPct: number;
  attributionWindowDays: number;
  holdDays: number;
  minPayoutCents: number;
  currency: string;
}

export interface Creator {
  creatorId: string;
  name: string;
  email: string;
  referralCode: string;
  status: "active" | "paused";
  discountPercent?: number;
  oneLinkUrl?: string | null;
  offerCodeBatchIos?: string | null;
  offerCodeBatchAndroid?: string | null;
  payoutMethod?: string;
  payoutHandle?: string | null;
  notes?: string | null;
  terms: CommissionTerms;
  createdAt?: string;
  updatedAt?: string;
}

export type CommissionStatus = "pending" | "approved" | "paid" | "void";

export interface Commission {
  commissionId: string;
  creatorId: string;
  userEmail: string;
  type: "signup" | "conversion";
  amountCents: number;
  currency: string;
  status: CommissionStatus;
  createdAt: string;
  availableAt?: string;
  approvedAt?: string;
  paidAt?: string;
  payoutBatchId?: string;
  sourceRef?: Record<string, any>;
}

export interface CreatorStats {
  creatorId: string;
  signups: number;
  conversions: number;
  earningsCents: Record<CommissionStatus, number>;
  commissions: Commission[];
}

export interface PayoutOwed {
  creatorId: string;
  name: string;
  email: string;
  payoutMethod?: string;
  payoutHandle?: string | null;
  owedCents: number;
  currency: string;
  minPayoutCents: number;
  readyToPay: boolean;
  commissionIds: string[];
}

export interface NewCreatorPayload {
  name: string;
  email: string;
  referralCode?: string;
  discountPercent?: number;
  oneLinkUrl?: string;
  payoutMethod?: string;
  payoutHandle?: string;
  notes?: string;
  terms?: Partial<CommissionTerms>;
}

// ── reads ────────────────────────────────────────────────────────────────────
export async function listCreators(): Promise<{ creators: Creator[]; defaults: CommissionTerms }> {
  return apiClient.get(`${BASE}?action=list_creators`);
}

export async function getCreatorStats(creatorId: string): Promise<CreatorStats> {
  return apiClient.get(`${BASE}?action=creator_stats&creatorId=${encodeURIComponent(creatorId)}`);
}

export async function getLedger(params?: {
  creatorId?: string;
  status?: CommissionStatus;
}): Promise<{ commissions: Commission[] }> {
  const q = new URLSearchParams({ action: "ledger" });
  if (params?.creatorId) q.set("creatorId", params.creatorId);
  if (params?.status) q.set("status", params.status);
  return apiClient.get(`${BASE}?${q.toString()}`);
}

export async function getPayoutsOwed(): Promise<{ payouts: PayoutOwed[] }> {
  return apiClient.get(`${BASE}?action=payouts_owed`);
}

// ── writes ───────────────────────────────────────────────────────────────────
export async function createCreator(payload: NewCreatorPayload): Promise<{ creator: Creator }> {
  return apiClient.post(BASE, { action: "create_creator", ...payload });
}

export async function updateCreator(
  payload: Partial<Creator> & { creatorId: string }
): Promise<{ creator: Creator }> {
  return apiClient.post(BASE, { action: "update_creator", ...payload });
}

export async function approveCommissions(creatorId?: string): Promise<{ approved: number }> {
  return apiClient.post(BASE, { action: "approve_commissions", creatorId });
}

export async function markPaid(
  creatorId: string,
  payoutBatchId?: string
): Promise<{ paid: number; paidCents: number; payoutBatchId: string }> {
  return apiClient.post(BASE, { action: "mark_paid", creatorId, payoutBatchId });
}

export async function voidCommission(
  commissionId: string,
  reason: string
): Promise<{ voided: string }> {
  return apiClient.post(BASE, { action: "void_commission", commissionId, reason });
}

// ── helpers ──────────────────────────────────────────────────────────────────
export function centsToUsd(cents?: number): string {
  return `$${((cents ?? 0) / 100).toFixed(2)}`;
}
